import React, { useCallback, useEffect, useRef, useState } from 'react';
import { EditorState, Compartment } from '@codemirror/state';
import {
  EditorView,
  keymap,
  lineNumbers,
  highlightActiveLine,
  highlightActiveLineGutter,
  tooltips,
} from '@codemirror/view';
import { defaultKeymap, history, historyKeymap, indentWithTab } from '@codemirror/commands';
import { autocompletion, closeBrackets, closeBracketsKeymap, completionKeymap } from '@codemirror/autocomplete';
import { searchKeymap, highlightSelectionMatches } from '@codemirror/search';
import { foldGutter, foldKeymap, bracketMatching } from '@codemirror/language';
import { lintKeymap } from '@codemirror/lint';
import { propalgos } from './codemirror/themes/propalgosTheme';
import { getLanguageForFile, getFileKind } from './codemirror/extensions/languageMap';
import { MarkdownPreview } from './MarkdownPreview';
import { getNotebookService } from '../../services/NotebookService';

export type TextFileSaveStatus = 'idle' | 'saved' | 'saving' | 'error';

interface TextFileEditorProps {
  path: string;
  sessionId: string;
  token: string;
  isReadOnly?: boolean;
  onDirtyChange?: (isDirty: boolean) => void;
  onSaveStatusChange?: (status: TextFileSaveStatus, message?: string) => void;
}

const AUTOSAVE_INTERVAL_MS = 30000;
const DEBOUNCE_MS = 300;

export const TextFileEditor: React.FC<TextFileEditorProps> = ({
  path,
  sessionId,
  token,
  isReadOnly = false,
  onDirtyChange,
  onSaveStatusChange,
}) => {
  const fileKind = getFileKind(path);
  const isMarkdown = fileKind === 'markdown';

  const editorRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const languageConf = useRef(new Compartment());
  const readOnlyConf = useRef(new Compartment());

  const contentRef = useRef<string>('');
  const isDirtyRef = useRef(false);
  const onDirtyChangeRef = useRef(onDirtyChange);
  onDirtyChangeRef.current = onDirtyChange;
  const onSaveStatusChangeRef = useRef(onSaveStatusChange);
  onSaveStatusChangeRef.current = onSaveStatusChange;

  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [source, setSource] = useState<string>('');
  // markdown-only: 'preview' is rendered view, 'source' is the editor
  const [mdMode, setMdMode] = useState<'preview' | 'source'>(isMarkdown ? 'preview' : 'source');

  const markDirty = useCallback(() => {
    if (!isDirtyRef.current) {
      isDirtyRef.current = true;
      onDirtyChangeRef.current?.(true);
    }
  }, []);

  const saveNow = useCallback(async () => {
    if (isReadOnly || !isDirtyRef.current) return;
    const service = getNotebookService({ sessionId, token });
    const content = contentRef.current;
    onSaveStatusChangeRef.current?.('saving');
    try {
      await service.saveTextFile(path, content);
      isDirtyRef.current = false;
      onDirtyChangeRef.current?.(false);
      onSaveStatusChangeRef.current?.('saved');
    } catch (err) {
      console.error('Failed to save text file:', err);
      onSaveStatusChangeRef.current?.('error', (err as Error)?.message);
    }
  }, [path, sessionId, token, isReadOnly]);

  // Load file contents
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoadError(null);
      setLoaded(false);
      try {
        const service = getNotebookService({ sessionId, token });
        const text = await service.loadTextFile(path);
        if (cancelled) return;
        contentRef.current = text;
        setSource(text);
        setLoaded(true);
        onSaveStatusChangeRef.current?.('idle');
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load text file:', err);
        setLoadError((err as Error)?.message || 'Failed to load file');
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [path, sessionId, token]);

  // Create CodeMirror view when we enter source mode
  useEffect(() => {
    if (!loaded) return;
    // Markdown preview mode has no editor
    if (isMarkdown && mdMode === 'preview') return;
    if (!editorRef.current) return;

    const languageExt = getLanguageForFile(path);

    let debounceId: number | null = null;
    const extensions = [
      lineNumbers(),
      highlightActiveLine(),
      highlightActiveLineGutter(),
      highlightSelectionMatches(),
      bracketMatching(),
      closeBrackets(),
      foldGutter(),
      history(),
      tooltips({ position: 'fixed' }),
      languageConf.current.of(languageExt ? [languageExt] : []),
      readOnlyConf.current.of(EditorState.readOnly.of(isReadOnly)),
      autocompletion({ activateOnTyping: true }),
      keymap.of([
        {
          key: 'Mod-s',
          preventDefault: true,
          run: () => {
            saveNow();
            return true;
          },
        },
        ...closeBracketsKeymap,
        ...defaultKeymap,
        ...historyKeymap,
        ...searchKeymap,
        ...completionKeymap,
        ...foldKeymap,
        ...lintKeymap,
        indentWithTab,
      ]),
      propalgos,
      EditorView.updateListener.of((update) => {
        if (!update.docChanged) return;
        const newValue = update.state.doc.toString();
        contentRef.current = newValue;
        markDirty();
        if (debounceId !== null) window.clearTimeout(debounceId);
        debounceId = window.setTimeout(() => {
          setSource(newValue);
          debounceId = null;
        }, DEBOUNCE_MS);
      }),
    ];

    const state = EditorState.create({ doc: contentRef.current, extensions });
    const view = new EditorView({ state, parent: editorRef.current });
    viewRef.current = view;

    return () => {
      if (debounceId !== null) window.clearTimeout(debounceId);
      view.destroy();
      viewRef.current = null;
    };
  }, [loaded, isMarkdown, mdMode, path, isReadOnly, markDirty, saveNow]);

  // Auto-save interval
  useEffect(() => {
    if (isReadOnly) return;
    const id = window.setInterval(() => {
      if (isDirtyRef.current) {
        void saveNow();
      }
    }, AUTOSAVE_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [isReadOnly, saveNow]);

  // Save on unmount if dirty
  useEffect(() => {
    return () => {
      if (isDirtyRef.current && !isReadOnly) {
        // Fire-and-forget; component going away
        void saveNow();
      }
    };
  }, [isReadOnly, saveNow]);

  if (loadError) {
    return (
      <div className="flex items-center justify-center h-full text-red-400">
        <div className="text-center">
          <div className="mb-2">Failed to load {path}</div>
          <div className="text-xs text-gray-500">{loadError}</div>
        </div>
      </div>
    );
  }

  if (!loaded) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400">
        <div className="text-sm">Loading {path}…</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-transparent">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-800/50 bg-black/30 backdrop-blur-sm text-xs text-gray-400">
        <div className="flex items-center gap-3">
          <span className="truncate">{path}</span>
          {isReadOnly && <span className="px-1.5 py-0.5 bg-yellow-900/40 text-yellow-300 border border-yellow-700/40">read-only</span>}
        </div>
        <div className="flex items-center gap-2">
          {isMarkdown && (
            <button
              type="button"
              onClick={() => setMdMode((m) => (m === 'preview' ? 'source' : 'preview'))}
              className="px-2 py-1 bg-gray-800/60 hover:bg-gray-700/60 border border-gray-700/50 text-gray-200"
            >
              {mdMode === 'preview' ? 'Edit source' : 'Preview'}
            </button>
          )}
          {!isReadOnly && (
            <button
              type="button"
              onClick={() => void saveNow()}
              className="px-2 py-1 bg-gray-800/60 hover:bg-gray-700/60 border border-gray-700/50 text-gray-200"
              title="Save (⌘S)"
            >
              Save
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 min-h-0 overflow-auto">
        {isMarkdown && mdMode === 'preview' ? (
          <MarkdownPreview
            source={source}
            sessionId={sessionId}
            placeholder="Empty markdown file — click Edit source to start writing."
            className="p-6 markdown-preview max-w-4xl"
            onClick={() => {
              if (!isReadOnly) setMdMode('source');
            }}
          />
        ) : (
          <div ref={editorRef} className="h-full" />
        )}
      </div>
    </div>
  );
};

export default TextFileEditor;
